import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { ECampaignStatus } from '@unfoldrtech/portal-mic/dist/src/models/core'

import {
  Container,
  StyledButton,
  Text,
  StyledSelect2,
  TAntSelectProps,
  TAntSelect2Props,
} from '../../Global'
import I18NText from '../../I18NText'
import { sanitizeDataTestId } from '../../../utils'
import {
  selectReportingFilters,
  setStatus,
} from '../../../store/reportingFilters'
import { setRefreshTable } from '../../../store/refreshTable'
import { setRefreshChart } from '../../../store/refreshChart'

export interface IStatusFilterForm {
  status?: ECampaignStatus[]
}

export const StatusFilter = ({
  statuses,
  selectionMode = 'multiple',
}: {
  statuses: Array<ECampaignStatus>
  selectionMode?: 'single' | 'multiple'
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { status: reportingStatus } = useSelector(selectReportingFilters)
  const [selectedStatuses, setSelectedStatuses] = useState<ECampaignStatus[]>(
    (reportingStatus as ECampaignStatus[] | undefined) || []
  )

  const methods = useForm<IStatusFilterForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = methods

  const onSubmit = async () => {
    dispatch(
      setStatus({
        status: selectedStatuses.length ? selectedStatuses : undefined,
      })
    )
    dispatch(setRefreshTable({ refreshTable: true }))
    dispatch(setRefreshChart({ refreshChart: true }))
    reset()
  }

  const isMultiple = selectionMode === 'multiple'

  const selectionModeProps: Pick<
    TAntSelect2Props<string>,
    'mode' | 'maxTagCount'
  > = isMultiple ? { mode: 'multiple', maxTagCount: 'responsive' } : {}

  return (
    <Container width="240px">
      <Container paddingLeft="var(--gutter-1x)" flex="1">
        <Text fontWeight={500}>
          <I18NText id="reporting.filter.select.status" preset="h6" />
        </Text>
      </Container>
      <Container paddingTop="var(--gutter-2x)" paddingBottom="var(--gutter-1x)">
        <Controller
          name="status"
          control={control}
          defaultValue={selectedStatuses}
          render={({ field }) => (
            <StyledSelect2
              id="status"
              {...field}
              {...selectionModeProps}
              value={isMultiple ? selectedStatuses : selectedStatuses[0]}
              padding="var(--gutter-1x)"
              error={errors.status}
              label={intl.formatMessage({
                id: 'reporting.filter.select.status.placeholder',
              })}
              options={statuses.map((st, index) => {
                return {
                  id: index,
                  label: intl.formatMessage({
                    id: st.toLowerCase(),
                  }),
                  value: st,
                  'data-testid': sanitizeDataTestId(`status-option-${st}`),
                }
              })}
              onChange={(value) => {
                const newValue = isMultiple
                  ? (value as ECampaignStatus[])
                  : [value as ECampaignStatus]
                setSelectedStatuses(newValue)
                field.onChange(newValue)
              }}
              data-testid="reporting-filter-status-select"
            />
          )}
        />
      </Container>
      <Container display="flex" justifyContent="right">
        <StyledButton
          onClick={handleSubmit(onSubmit)}
          width="100px"
          height="40px"
        >
          <Container
            display="grid"
            height="calc(40px - var(--gutter-2x))"
            padding="var(--gutter-mini)"
          >
            <I18NText
              id="reporting.filter.apply"
              preset="cta-sm"
              fontSize="var(--font-size-ps)"
              data-testid="reporting-filter-status-apply-button"
            />
          </Container>
        </StyledButton>
      </Container>
    </Container>
  )
}
